/*
 * Copyright 2026 Sony Corporation
 */

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Tooltip, Typography, alpha } from '@mui/material';

export interface CollapsedRailProps {
  /** Which edge of the viewport it sits on, which is also which way it reopens. */
  side: 'left' | 'right';
  label: string;
  onExpand: () => void;
  /** Floating over the viewport rather than beside it, so the scene shows through. */
  translucent?: boolean;
}

/**
 * A hidden panel, still on screen.
 *
 * Collapsing to nothing would leave no way back that is not the menu bar, so a
 * panel that is out of the way keeps a strip wide enough to name itself.
 */
export const CollapsedRail = ({ side, label, onExpand, translucent }: CollapsedRailProps) => (
  <Box
    className={`flex h-full w-9 shrink-0 flex-col items-center gap-1 py-2 ${side === 'left' ? 'border-r' : 'border-l'}`}
    sx={(theme) => ({
      bgcolor: translucent ? alpha(theme.palette.background.paper, 0.6) : 'background.paper',
      backdropFilter: translucent ? 'blur(8px)' : undefined,
      borderColor: 'divider',
    })}
  >
    <Tooltip title={`Show ${label.toLowerCase()}`} placement={side === 'left' ? 'right' : 'left'}>
      <IconButton size="small" aria-label={`Show ${label.toLowerCase()}`} onClick={onExpand}>
        {side === 'left' ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
    <Typography
      variant="caption"
      color="text.secondary"
      className="cursor-pointer select-none"
      sx={{ writingMode: 'vertical-rl' }}
      onClick={onExpand}
    >
      {label}
    </Typography>
  </Box>
);
